/**
 * FileValidationService
 *
 * Singleton service for validating chat file uploads.
 * Handles file type, size, and extension validation for different channels.
 */

import {
  FileCategory,
  FILE_SIZE_LIMITS,
  MIME_TYPE_MAPPING,
  ValidationError,
  isCategorySupported,
  getSizeLimitMB,
  getSupportedTypesString,
} from "../../config/fileRestrictions"

export interface FileValidationResult {
  fileType: string | undefined
  error: string | undefined
  maxSize: number
  /** Error details for better UI messaging */
  errorDetails?: {
    code: string
    supportedTypes?: string
    maxSizeMB?: number
  }
}

// Legacy exports for backward compatibility
export const DocumentMediaType: string[] = MIME_TYPE_MAPPING.document
export const ImageMediaType: string[] = ["jpeg", "png"]
export const LazadaImageMediaType: string[] = ["jpeg", "png", "gif"]
export const VideoMediaType: string[] = MIME_TYPE_MAPPING.video
export const StickerMediaType: string[] = MIME_TYPE_MAPPING.sticker
export const AudioMediaType: string[] = MIME_TYPE_MAPPING.audio

// Max file sizes in MB (using unified config)
const MAX_SIZES = FILE_SIZE_LIMITS

class FileValidationService {
  private static instance: FileValidationService

  static getInstance(): FileValidationService {
    if (!this.instance) {
      this.instance = new FileValidationService()
    }
    return this.instance
  }

  /**
   * Validates a file for chat upload
   * @param fileType - The MIME type category (e.g., "image", "video")
   * @param fileExtension - The file extension (e.g., "png", "mp4")
   * @param fileSize - File size in bytes
   * @param channel - The chat channel (e.g., "lazada", "whatsapp")
   * @returns FileValidationResult with fileType, error message, and maxSize
   */
  validateFile(
    fileType: string | undefined,
    fileExtension: string | undefined,
    fileSize: number,
    channel?: string
  ): FileValidationResult {
    let result: FileCategory | undefined = undefined
    let error: string | undefined = undefined
    let maxSize = 0
    let errorDetails: FileValidationResult["errorDetails"] = undefined

    // Check File Type
    if (fileType === undefined) {
      error = ValidationError.INVALID_TYPE
      errorDetails = { code: ValidationError.INVALID_TYPE }
    } else if (fileType.toLowerCase() === "application" || fileType.toLowerCase() === "text") {
      result = "document"
    } else if (fileType.toLowerCase() === "image") {
      result = "image"
    } else if (fileType.toLowerCase() === "video") {
      result = "video"
    } else if (fileType.toLowerCase() === "audio") {
      result = "audio"
    } else {
      error = ValidationError.INVALID_TYPE
      errorDetails = { code: ValidationError.INVALID_TYPE }
    }

    // Check Platform Support (before size check to give better error)
    if (result !== undefined && !error) {
      if (!isCategorySupported(result, channel)) {
        error = ValidationError.TYPE_NOT_SUPPORTED
        errorDetails = {
          code: ValidationError.TYPE_NOT_SUPPORTED,
          supportedTypes: getSupportedTypesString(channel),
        }
        return { fileType: result, error, maxSize: 0, errorDetails }
      }
    }

    // Check File Size
    if (result !== undefined && !error) {
      const sizeInMB = fileSize / (1024 * 1024)
      const limit = getSizeLimitMB(result)
      if (limit && sizeInMB >= limit) {
        maxSize = limit
        error = ValidationError.MAX_SIZE_EXCEEDED
        errorDetails = {
          code: ValidationError.MAX_SIZE_EXCEEDED,
          maxSizeMB: limit,
        }
      }
    }

    // Check File Extension
    if (result !== undefined && !error) {
      const extError = this.validateExtension(result, fileExtension, channel)
      if (extError) {
        error = extError
        errorDetails = { code: ValidationError.INVALID_FORMAT }
      }
    }

    return { fileType: result, error, maxSize, errorDetails }
  }

  /**
   * Validates file extension based on file type and channel
   * Uses unified restrictions - most restrictive formats only (no GIF)
   */
  private validateExtension(
    fileType: string,
    fileExtension: string | undefined,
    _channel?: string // Channel param kept for API compatibility but not used
  ): string | undefined {
    if (fileExtension === undefined) {
      return undefined
    }

    const ext = fileExtension.toLowerCase()
    const mimeTypes = MIME_TYPE_MAPPING[fileType as FileCategory]

    if (!mimeTypes) {
      return ValidationError.INVALID_FORMAT
    }

    // Check if extension is in the allowed MIME types
    if (!mimeTypes.includes(ext)) {
      return ValidationError.INVALID_FORMAT
    }

    return undefined
  }

  /**
   * Validates a File object
   * @param file - The File object to validate
   * @param channel - The chat channel
   * @returns FileValidationResult
   */
  validateFileObject(file: File, channel?: string): FileValidationResult {
    const mimeType = file.type
    const [type, extension] = mimeType.split("/")
    return this.validateFile(type, extension, file.size, channel)
  }

  /**
   * Formats file size in human-readable format
   * @param bytes - File size in bytes
   * @param decimals - Number of decimal places (default: 2)
   * @returns Formatted size string (e.g., "1.5 MB")
   */
  formatSize(bytes: number, decimals = 2): string {
    if (bytes === 0) return "0 Bytes"

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Byte", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }

  /**
   * Gets the file type category from MIME type
   * @param mimeType - Full MIME type (e.g., "image/png")
   * @returns File type category (e.g., "image", "document")
   */
  getFileTypeFromMime(mimeType: string): string {
    const type = mimeType.split("/")[0]

    switch (type.toLowerCase()) {
      case "image":
        return "image"
      case "video":
        return "video"
      case "audio":
        return "audio"
      case "application":
      case "text":
        return "document"
      default:
        return "document"
    }
  }

  /**
   * Checks if a file type is supported
   * @param mimeType - Full MIME type
   * @param channel - The chat channel
   * @returns boolean
   */
  isSupported(mimeType: string, channel?: string): boolean {
    const [type, extension] = mimeType.split("/")
    const result = this.validateFile(type, extension, 0, channel)
    return result.error === undefined || result.error === "MaxSize"
  }
}

export default FileValidationService
